import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { pageCount, Pagination } from "../../components/ui";
import { hideRunModelColumns } from "../../lib/featureFlags";
import {
  Empty,
  ErrorState,
  Loading,
} from "../../components/WorkspacePrimitives";
import {
  AGENT_RUN_PAGE_SIZE,
  useAgentCompanies,
  useAgentRunPage,
} from "../api/e2e";
import AgentRunTable from "../components/AgentRunTable";
import CompanyWorkspacePage from "../components/CompanyWorkspacePage";

export default function AgentCompanyRunsPage() {
  const { companyId = "" } = useParams<{ companyId: string }>();
  const companies = useAgentCompanies();
  const [page, setPage] = useState(1);
  const runs = useAgentRunPage({
    companyId,
    offset: (page - 1) * AGENT_RUN_PAGE_SIZE,
  });
  const company = companies.data?.find((item) => item.id === companyId);
  const total = runs.data?.total ?? 0;
  const pageSize = runs.data?.limit ?? AGENT_RUN_PAGE_SIZE;
  const currentPage = Math.min(page, pageCount(total, pageSize));
  const visibleRuns = runs.data?.items ?? [];
  useEffect(() => {
    if (runs.data && currentPage !== page) setPage(currentPage);
  }, [currentPage, page, runs.data]);

  if (
    (companies.isPending && !companies.data) ||
    (runs.isPending && !runs.data)
  ) {
    return <Loading label="Loading company runs…" />;
  }
  if (companies.error && !companies.data) {
    return (
      <ErrorState error={companies.error} onRetry={() => companies.refetch()} />
    );
  }
  if (runs.error && !runs.data) {
    return <ErrorState error={runs.error} onRetry={() => runs.refetch()} />;
  }
  if (!company) return <Empty>This company is not available.</Empty>;

  return (
    <CompanyWorkspacePage companies={companies.data ?? []} company={company}>
      <section className="company-tab-content" aria-label="Runs">
        {visibleRuns.length === 0 ? (
          <Empty>No run has been launched for this company.</Empty>
        ) : (
          <>
            <AgentRunTable
              runs={visibleRuns}
              showVersion
              showHypothesis={false}
              showModelColumns={!hideRunModelColumns()}
            />
            <Pagination
              page={currentPage}
              pageSize={pageSize}
              total={total}
              ariaLabel="Company run pages"
              onPageChange={setPage}
            />
          </>
        )}
      </section>
    </CompanyWorkspacePage>
  );
}
